<template>
  <v-dialog v-model="showDialog" max-width="1300" scrollable>
    <v-card>
      <v-card-title class="secondary white--text px-4 py-2">
        <span>Add Leg</span>
        <v-spacer></v-spacer>
        <v-btn icon @click="closeDialog">
          <v-icon class="white--text">mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-4">
        <div
          v-if="info"
          class="d-flex flex-wrap mb-3 pa-3 rounded"
          style="gap: 24px; background-color: rgba(0, 0, 0, 0.03)"
        >
          <div>
            <span class="text-caption grey--text">Customer</span>
            <p class="text-body-2 font-weight-medium mb-0">{{ info.customer || "—" }}</p>
          </div>
          <div>
            <span class="text-caption grey--text">Transporter</span>
            <p class="text-body-2 font-weight-medium mb-0">{{ info.transporter || "—" }}</p>
          </div>
          <div>
            <span class="text-caption grey--text">Route</span>
            <p class="text-body-2 font-weight-medium mb-0">{{ info.route || "—" }}</p>
          </div>
          <div>
            <span class="text-caption grey--text">Container Type</span>
            <p class="text-body-2 font-weight-medium mb-0">{{ info.containerType || "—" }}</p>
          </div>
          <div>
            <span class="text-caption grey--text">Movement Date</span>
            <p class="text-body-2 font-weight-medium mb-0">{{ info.movementDate || "—" }}</p>
          </div>
        </div>

        <div class="d-flex align-center flex-wrap mb-2" style="gap: 6px">
          <v-chip
            small
            outlined
            :color="activeChipKey === 'da_numbers' || chipValues.da_numbers ? 'primary' : 'default'"
            :close="!!chipValues.da_numbers"
            @click="toggleChip('da_numbers')"
            @click:close="clearChip('da_numbers')"
          >
            <v-icon small left>mdi-tag-outline</v-icon> DA no.
          </v-chip>

          <v-chip
            small
            outlined
            :color="activeChipKey === 'container_numbers' || chipValues.container_numbers ? 'primary' : 'default'"
            :close="!!chipValues.container_numbers"
            @click="toggleChip('container_numbers')"
            @click:close="clearChip('container_numbers')"
          >
            <v-icon small left>mdi-tag-outline</v-icon> Container no.
          </v-chip>

          <v-chip
            small
            outlined
            :color="activeChipKey === 'sub_hire_ids' || chipValues.sub_hire_ids ? 'primary' : 'default'"
            :close="!!chipValues.sub_hire_ids"
            @click="toggleChip('sub_hire_ids')"
            @click:close="clearChip('sub_hire_ids')"
          >
            <v-icon small left>mdi-tag-outline</v-icon> Sub hire no.
          </v-chip>

          <template v-if="activeChipKey">
            <v-text-field
              v-model="chipInput"
              dense
              outlined
              hide-details
              :placeholder="'Enter ' + activeChipLabel.toLowerCase() + '....'"
              style="min-width: 260px"
              @keyup.enter="applyChipFilter"
              autofocus
              append-icon="mdi-close"
              @click:append="closeChipInput"
            />
          </template>

          <v-chip v-if="!activeChipKey && selectedCount > 0" x-small color="primary" outlined class="ml-2">
            {{ selectedCount }} selected
          </v-chip>
        </div>

        <div class="d-flex flex-wrap mb-2" style="gap: 24px">
          <div>
            <span class="text-caption">Revenue</span>
            <p class="text-subtitle-1 font-weight-medium mb-0">{{ previewRevenue }} {{ $globals.currency }}</p>
          </div>
          <div>
            <span class="text-caption">Additional Charge</span>
            <p class="text-subtitle-1 font-weight-medium mb-0">{{ previewAdditionalCharge }} {{ $globals.currency }}</p>
          </div>
          <div>
            <span class="text-caption">Total Revenue</span>
            <p class="text-subtitle-1 font-weight-medium mb-0">{{ previewTotalRevenue }} {{ $globals.currency }}</p>
          </div>
          <div>
            <span class="text-caption">Taxable Amount</span>
            <p class="text-subtitle-1 font-weight-medium mb-0">{{ previewTotalTaxable }} {{ $globals.currency }}</p>
          </div>
          <div>
            <span class="text-caption">Total Revenue With Tax</span>
            <p class="text-subtitle-1 font-weight-medium mb-0">{{ previewTotalRevenueWithTax }} {{ $globals.currency }}</p>
          </div>
          <div>
            <span class="text-caption">Total Cost</span>
            <p class="text-subtitle-1 font-weight-medium mb-0">{{ previewTotalCost }} {{ $globals.currency }}</p>
          </div>
          <div>
            <span class="text-caption">Total Cost With Tax</span>
            <p class="text-subtitle-1 font-weight-medium mb-0">{{ previewTotalCostWithTax }} {{ $globals.currency }}</p>
          </div>
          <div>
            <span class="text-caption">Selected Legs</span>
            <p class="text-subtitle-1 font-weight-medium mb-0">{{ selectedCount }}</p>
          </div>
        </div>

        <AgGridVue
          style="width: 100%; height: 50vh"
          class="ag-theme-alpine"
          rowSelection="multiple"
          id="addLegGrid"
          :columnDefs="headers"
          :context="context"
          @grid-ready="onGridReady"
          :defaultColDef="defaultColDef"
          :grid-options="gridOptions"
          :rowData="legList"
          @row-selected="onRowSelected"
        ></AgGridVue>

        <v-row v-if="!hasActiveChipSearch && totalItems > 0" class="ma-0 d-flex justify-end py-2">
          <Pagination
            :pageNo="pageNo"
            :totalItems="totalItems"
            :pageSize="itemsPerPage"
            :itemsPerPageValues="[10, 20, 50, 100]"
            @itemsPerPageChange="itemsPerPageChanged"
            @prevPage="prevPage"
            @nextPage="nextPage"
          />
        </v-row>
      </v-card-text>

      <v-divider></v-divider>
      <v-card-actions class="d-flex justify-end">
        <v-btn text @click="closeDialog">Cancel</v-btn>
        <v-btn
          color="primary"
          depressed
          :disabled="selectedCount === 0"
          @click="showConfirm = true"
        >
          Submit
        </v-btn>
      </v-card-actions>
    </v-card>

    <NewConfirmationDialog
      v-model="showConfirm"
      icon="mdi-help-circle"
      status="primary"
      title="Confirm Add Leg"
      :text="confirmText"
      confirmButton="Confirm"
      rejectButton="Cancel"
      @confirmed="confirmAdd"
    />
  </v-dialog>
</template>

<script>
import { AgGridVue } from "ag-grid-vue";
import NewConfirmationDialog from "@/components/General/NewConfirmationDialog.vue";
import Pagination from "@/components/General/Pagination.vue";
import StatusChip from "@/components/AgGridComponents/General/StatusChip.vue";

export default {
  components: {
    AgGridVue,
    NewConfirmationDialog,
    Pagination,
    StatusChip,
  },

  props: {
    value: { type: Boolean, default: false },
    legRow: { default: null },
    info: { type: Object, default: null },
    excludeLegIds: { type: Array, default: () => [] },
    baseRevenue: { type: Number, default: 0 },
    baseAdditionalCharge: { type: Number, default: 0 },
    baseTotalRevenue: { type: Number, default: 0 },
    baseTaxableAmount: { type: Number, default: 0 },
    baseTotalRevenueWithTax: { type: Number, default: 0 },
    baseLegCount: { type: Number, default: 0 },
    baseTotalCost: { type: Number, default: 0 },
    baseTotalCostWithTax: { type: Number, default: 0 },
    transporterRate: { type: Number, default: 0 },
    transporterTaxPercentage: { type: Number, default: 0 },
    chargesTotal: { type: Number, default: 0 },
  },

  data() {
    return {
      gridApi: null,
      gridColumnApi: null,
      selectedRowsMap: {},
      activeChipKey: null,
      chipInput: "",
      chipValues: { da_numbers: "", container_numbers: "", sub_hire_ids: "" },
      isRestoringSelection: false,

      legList: [],
      pageNo: 1,
      itemsPerPage: 100,
      totalItems: 0,

      headers: [
        {
          headerName: "",
          field: "select",
          width: 50,
          minWidth: 50,
          maxWidth: 50,
          suppressSizeToFit: true,
          checkboxSelection: (params) => {
            return !!(params?.data?.sub_hire_legs_checkbox);
          },
          headerCheckboxSelection: true,
          headerCheckboxSelectionFilteredOnly: true,
        },
        { headerName: "Leg", field: "leg_id", minWidth: 140 },
        { headerName: "DA", field: "da_no", minWidth: 110 },
        { headerName: "Customer", field: "customer_name", minWidth: 140, tooltipField: "customer_name" },
        { headerName: "Company", field: "company_name", minWidth: 140, tooltipField: "company_name" },
        { headerName: "Route", field: "leg_lane_title", minWidth: 110 },
        { headerName: "Container Type", field: "container_type_name", minWidth: 120 },
        { headerName: "Container No", field: "sub_hire_leg_containers", minWidth: 120 },
        { headerName: "Movement Date", field: "movement_date", minWidth: 120 },
        { headerName: "Sub Hire No.", field: "sub_hire_no", minWidth: 120 },
        { headerName: "Rate (AED)", field: "leg_rate", minWidth: 110 },
        {
          headerName: "Status",
          field: "status",
          minWidth: 140,
          cellRenderer: "StatusChip",
          cellRendererParams: (params) => ({ status: params.value }),
        },
      ],

      defaultColDef: { resizable: true, flex: 1 },
      gridOptions: {
        onGridSizeChanged: () => {
          this.resizeGrid();
        },
        suppressRowClickSelection: true,
        suppressDragLeaveHidesColumns: true,
        enableCellTextSelection: true,
        tooltipShowDelay: 0,
        rowHeight: 35,
      },

      showConfirm: false,
    };
  },

  computed: {
    showDialog: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      },
    },
    context() {
      return { parentComponent: this };
    },
    offset() {
      return this.itemsPerPage * (this.pageNo - 1);
    },
    hasActiveChipSearch() {
      return Object.values(this.chipValues).some((v) => !!v);
    },
    activeChipLabel() {
      const labels = { da_numbers: "DA no", container_numbers: "Container no", sub_hire_ids: "Sub hire no" };
      return labels[this.activeChipKey] || "";
    },
    selectedRows() {
      return Object.values(this.selectedRowsMap);
    },
    selectedCount() {
      return this.selectedRows.length;
    },
    deltaRevenue() {
      let total = 0;
      this.selectedRows.forEach((row) => {
        total += parseFloat(row.leg_rate || 0);
      });
      return total;
    },
    deltaAdditionalCharge() {
      let total = 0;
      this.selectedRows.forEach((row) => {
        total += parseFloat(row.customer_da_total_additional_charge || 0);
      });
      return total;
    },
    deltaTaxableAmount() {
      let total = 0;
      this.selectedRows.forEach((row) => {
        total += parseFloat(row.customer_da_total_taxable_amount || 0);
      });
      return total;
    },
    previewRevenue() {
      return (this.baseRevenue + this.deltaRevenue).toFixed(2);
    },
    previewAdditionalCharge() {
      return (this.baseAdditionalCharge + this.deltaAdditionalCharge).toFixed(2);
    },
    previewTotalRevenue() {
      return (this.baseTotalRevenue + this.deltaRevenue + this.deltaAdditionalCharge).toFixed(2);
    },
    previewTotalTaxable() {
      return (this.baseTaxableAmount + this.deltaTaxableAmount).toFixed(2);
    },
    previewTotalRevenueWithTax() {
      return (
        this.baseTotalRevenueWithTax +
        this.deltaRevenue +
        this.deltaAdditionalCharge +
        this.deltaTaxableAmount
      ).toFixed(2);
    },
    previewTotalCost() {
      const perLegCost = this.transporterRate + this.chargesTotal;
      return (this.baseTotalCost + perLegCost * this.selectedCount).toFixed(2);
    },
    previewTotalCostWithTax() {
      const perLegRateTax = (this.transporterRate * this.transporterTaxPercentage) / 100;
      const deltaCost = (this.transporterRate + this.chargesTotal) * this.selectedCount;
      const deltaTax = perLegRateTax * this.selectedCount;
      return (this.baseTotalCostWithTax + deltaCost + deltaTax).toFixed(2);
    },
    confirmText() {
      return `Total Revenue: <b>${this.previewTotalRevenue} ${this.$globals.currency}</b><br/>Total Taxable Amount: <b>${this.previewTotalTaxable} ${this.$globals.currency}</b><br/>Total Cost: <b>${this.previewTotalCost} ${this.$globals.currency}</b><br/>Total Cost With Tax: <b>${this.previewTotalCostWithTax} ${this.$globals.currency}</b><br/>Selected Legs: <b>${this.selectedCount}</b>`;
    },
  },

  watch: {
    value(val) {
      if (val) {
        this.selectedRowsMap = {};
        this.activeChipKey = null;
        this.chipInput = "";
        this.chipValues = { da_numbers: "", container_numbers: "", sub_hire_ids: "" };
        this.pageNo = 1;
        this.getLegList();
      }
    },
  },

  methods: {
    closeDialog() {
      this.showDialog = false;
    },

    resizeGrid() {
      if (this.gridOptions && "api" in this.gridOptions) {
        setTimeout(() => {
          this.gridOptions.api.sizeColumnsToFit();
        }, 100);
      }
    },

    onGridReady(params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;
      this.resizeGrid();
    },

    getLegList() {
      if (this.hasActiveChipSearch) {
        this.searchLegsByChips();
      } else {
        this.getLegListPaginated();
      }
    },

    getLegListPaginated() {
      this.$bus.$emit("showLoader", true);
      const apiParams = { leg_id: this.legRow, limit: this.itemsPerPage, offset: this.offset };
      this.$api.subHire
        .getLegListFromLane(this.legRow, apiParams)
        .then((res) => {
          const rows = (res.data || []).filter((row) => !this.excludeLegIds.includes(row.id));
          this.legList = rows;
          this.totalItems = res.count != null ? res.count : rows.length;
          this.$nextTick(() => this.setSelectedValues());
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },

    searchLegsByChips() {
      this.$bus.$emit("showLoader", true);
      const apiParams = { leg_id: this.legRow };
      Object.entries(this.chipValues).forEach(([k, v]) => {
        if (v) apiParams[k] = v;
      });
      this.$api.subHire
        .getLegs(apiParams)
        .then((res) => {
          const rows = (res.data || []).filter((row) => !this.excludeLegIds.includes(row.id));
          this.legList = rows;
          this.totalItems = rows.length;
          this.$nextTick(() => this.setSelectedValues());
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },

    setSelectedValues() {
      if (!this.gridApi) return;
      this.isRestoringSelection = true;
      this.gridApi.forEachNode((node) => {
        if (node.data && this.selectedRowsMap[node.data.id]) {
          node.setSelected(true);
        }
      });
      this.isRestoringSelection = false;
    },

    itemsPerPageChanged(e) {
      this.pageNo = 1;
      this.itemsPerPage = e;
      this.getLegList();
    },

    prevPage() {
      this.pageNo--;
      this.getLegList();
    },

    nextPage() {
      this.pageNo++;
      this.getLegList();
    },

    onRowSelected(event) {
      if (this.isRestoringSelection || !event.data) return;
      const legId = event.data.id;
      if (event.node.isSelected()) {
        this.$set(this.selectedRowsMap, legId, event.data);
      } else {
        this.$delete(this.selectedRowsMap, legId);
      }
    },

    toggleChip(key) {
      if (this.activeChipKey === key) {
        this.activeChipKey = null;
        this.chipInput = "";
      } else {
        this.activeChipKey = key;
        this.chipInput = this.chipValues[key] || "";
      }
    },

    applyChipFilter() {
      if (!this.chipInput.trim() || !this.activeChipKey) return;
      const normalized = this.chipInput
        .trim()
        .replace(/[\r\n\t]+/g, ",")
        .replace(/\s*,\s*/g, ",")
        .replace(/\s+/g, ",")
        .replace(/,+/g, ",")
        .replace(/^,|,$/g, "");
      this.chipInput = normalized;
      this.chipValues[this.activeChipKey] = normalized;
      this.pageNo = 1;
      this.getLegList();
    },

    closeChipInput() {
      const key = this.activeChipKey;
      this.chipValues[key] = "";
      this.activeChipKey = null;
      this.chipInput = "";
      this.pageNo = 1;
      this.getLegList();
    },

    clearChip(key) {
      this.chipValues[key] = "";
      this.activeChipKey = null;
      this.chipInput = "";
      this.pageNo = 1;
      this.getLegList();
    },

    confirmAdd() {
      this.$emit("confirm", this.selectedRows);
      this.selectedRowsMap = {};
      this.showDialog = false;
    },
  },
};
</script>
